import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { useCallback, useMemo } from "react";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { CalendarTeamScreenQuery } from "./__generated__/CalendarTeamScreenQuery.graphql";
import {
  Box,
  TouchableBox,
  useSpaceSize,
  useSubscriptionWithSkip,
  useTheme,
} from "arkh";
import { ActionFooter } from "@/src/components/ActionFooter";
import { CalendarTeamScreenComponent_team$key } from "./__generated__/CalendarTeamScreenComponent_team.graphql";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ActivityIndicator } from "react-native";
import { CalendarTeamEventItem } from "./CalendarTeamEventItem";
import { CalendarTeamWeekItem } from "./CalendarTeamWeekItem";
import { FlashList } from "@shopify/flash-list";
import { createCalendarOverviewListData } from "widgets/src/WidgetCalendarOverview/createCalendarOverviewListData";
import { CalendarTeamEventDeletedSubscription } from "./__generated__/CalendarTeamEventDeletedSubscription.graphql";
import { CalendarTeamEventUpdatedSubscription } from "./__generated__/CalendarTeamEventUpdatedSubscription.graphql";
import { CalendarTeamEventCreatedSubscription } from "./__generated__/CalendarTeamEventCreatedSubscription.graphql";

type Props = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "CalendarTeamScreen">,
    UserLoggedInNavigatorNavigationProp<"CalendarTeam">
  >;
  route: RouteProp<ParamList, "CalendarTeamScreen">;
};

function InnerCalendarTeamScreenComponent(
  props: Props & {
    team: CalendarTeamScreenComponent_team$key;
    canEdit: boolean;
  }
) {
  const intl_tokens = useIntlTokens();
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment CalendarTeamScreenComponent_team on Team
      @refetchable(queryName: "CalendarTeamScreenComponentPaginationQuery") {
        calendars(includeExternal: false) {
          id
        }
        calendarEvents(first: $first, after: $after)
          @connection(key: "CalendarTeamScreenComponent_calendarEvents") {
          __id
          edges {
            node {
              id
              startAt {
                isoString
              }
              endAt {
                isoString
              }
              createdAt {
                isoString
              }
              isAllDayEvent
              ...CalendarTeamEventItem_event
            }
          }
        }
      }
    `,
    props.team
  );

  useSubscriptionWithSkip<CalendarTeamEventCreatedSubscription>(
    useMemo(
      () => ({
        variables: {
          calendarIds: data.calendars?.map((calendar) => calendar.id) ?? [],
          connections: data.calendarEvents?.__id
            ? [data.calendarEvents.__id]
            : [],
        },
        subscription: graphql`
          subscription CalendarTeamEventCreatedSubscription(
            $calendarIds: [ID!]!
            $connections: [ID!]!
          ) {
            calendarEventCreated(calendarIds: $calendarIds) {
              calendarEvent
                @prependNode(
                  connections: $connections
                  edgeTypeName: "CalendarTeamCalendarEventsEdge"
                ) {
                id
                startAt {
                  isoString
                }
                endAt {
                  isoString
                }
                createdAt {
                  isoString
                }
                isAllDayEvent
                ...CalendarTeamEventItem_event
              }
            }
          }
        `,
      }),
      [data.calendars, data.calendarEvents?.__id]
    )
  );

  useSubscriptionWithSkip<CalendarTeamEventDeletedSubscription>(
    useMemo(
      () => ({
        variables: {
          calendarIds: data.calendars?.map((calendar) => calendar.id) ?? [],
          connections: data.calendarEvents?.__id
            ? [data.calendarEvents.__id]
            : [],
        },
        subscription: graphql`
          subscription CalendarTeamEventDeletedSubscription(
            $calendarIds: [ID!]!
            $connections: [ID!]!
          ) {
            calendarEventDeleted(calendarIds: $calendarIds) {
              calendarEvent {
                id @deleteEdge(connections: $connections)
              }
            }
          }
        `,
      }),
      [data.calendarEvents?.__id, data.calendars]
    )
  );

  useSubscriptionWithSkip<CalendarTeamEventUpdatedSubscription>(
    useMemo(
      () => ({
        variables: {
          calendarIds: data.calendars?.map((calendar) => calendar.id) ?? [],
        },
        subscription: graphql`
          subscription CalendarTeamEventUpdatedSubscription(
            $calendarIds: [ID!]!
          ) {
            calendarEventUpdated(calendarIds: $calendarIds) {
              calendarEvent {
                id
                startAt {
                  isoString
                }
                endAt {
                  isoString
                }
                createdAt {
                  isoString
                }
                isAllDayEvent
                ...CalendarTeamEventItem_event
              }
            }
          }
        `,
      }),
      [data.calendars]
    )
  );

  const listData = useMemo(() => {
    return createCalendarOverviewListData(data.calendarEvents?.edges ?? []);
  }, [data.calendarEvents?.edges]);

  const spaceMedium = useSpaceSize("medium");
  const theme = useTheme();
  const safeAreaInsets = useSafeAreaInsets();

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      if (item.itemType === "week") {
        return (
          <CalendarTeamWeekItem
            isFirst={index === 0}
            date={item.date}
            numberOfEvents={item.numberOfEvents}
          />
        );
      }
      return (
        <TouchableBox
          onPress={() => {
            props.navigation.navigate("CalendarTeamEventEdit", {
              teamId: props.route.params.teamId,
              eventId: item.id,
              connections: data.calendarEvents?.__id
                ? [data.calendarEvents.__id]
                : [],
            });
          }}
          disabled={!props.canEdit}
          spaceVertical="xsmall"
          spaceHorizontal="medium"
        >
          <CalendarTeamEventItem event={item} isActive={item.isActive} />
        </TouchableBox>
      );
    },
    [
      props.canEdit,
      props.navigation,
      props.route.params.teamId,
      data.calendarEvents?.__id,
    ]
  );

  const keyExtractor = useCallback((item: (typeof listData)[number]) => {
    if (item.itemType === "week") {
      return `week-${item.date.toISO()}`;
    }
    return item.id;
  }, []);

  const ListFooterComponent = useMemo(() => {
    return isLoadingNext ? (
      <Box space="medium">
        <ActivityIndicator size="small" color={theme["status-inactive"]} />
      </Box>
    ) : null;
  }, [isLoadingNext, theme]);

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(10);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  return (
    <>
      <FlashList
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingBottom: spaceMedium + safeAreaInsets.bottom,
        }}
        data={listData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListFooterComponent={ListFooterComponent}
        onEndReached={onEndReached}
      />
      {(data.calendars?.length ?? 0) > 0 ? (
        <ActionFooter
          primary={
            <ActionFooter.Primary
              title={intl_tokens.AddCalendarEvent}
              icon="add"
              onPress={() => {
                props.navigation.navigate("CalendarTeamEventCreate", {
                  teamId: props.route.params.teamId,
                  connections: data.calendarEvents?.__id
                    ? [data.calendarEvents.__id]
                    : [],
                });
              }}
            />
          }
        />
      ) : null}
    </>
  );
}

function CalendarTeamScreenComponent(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<CalendarTeamScreenQuery>(
    graphql`
      query CalendarTeamScreenQuery($teamId: ID!, $first: Int, $after: String) {
        store {
          team(id: $teamId) {
            ...CalendarTeamScreenComponent_team
          }
        }
      }
    `,
    {
      teamId: props.route.params.teamId,
      first: 30,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  return (
    <>
      {data.store.team ? (
        <InnerCalendarTeamScreenComponent
          team={data.store.team}
          navigation={props.navigation}
          route={props.route}
          canEdit
        />
      ) : null}
    </>
  );
}

function CalendarTeamScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => <CalendarTeamScreenComponent {...props} fetchKey={fk} />}
    </LazyQueryNetworkBoundrary>
  );
}

type ParamList = {
  CalendarTeamScreen: {
    teamId: string;
  };
};

const Stack = createNativeStackNavigator<ParamList>();

export const CalendarTeam = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"CalendarTeam">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    const intl_tokens = useIntlTokens();
    const theme = useTheme();
    return (
      <Stack.Navigator initialRouteName="CalendarTeamScreen">
        <Stack.Screen
          name="CalendarTeamScreen"
          component={CalendarTeamScreen}
          initialParams={{
            teamId: props.route.params.teamId,
          }}
          options={{
            ...navigationHeaderOptions({
              type: "CLOSE",
              onClose: () => props.navigation.goBack(),
              title: intl_tokens.Calendars,
              defaultContentStyle: true,
            }),
            contentStyle: { backgroundColor: theme["surface-primary"] },
          }}
        />
      </Stack.Navigator>
    );
  }
);
